import { ACL_TYPE } from '@openx/types/aclType.ts';
import { type RboConfigField, rboConfigField } from '@openx/types/deal.ts';
import { getRboTranslations } from '@openx/types/rbo.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type Field = {
  field: RboConfigField;
  options?: Record<string, string>;
};

type Props = {
  readonly?: boolean;
};

export const useRboFields = ({ readonly = false }: Props = {}) => {
  const { t } = useTranslation();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const isAdvancedAllowed = useMemo(() => {
    const adminAclType = readonly ? ACL_TYPE.DEAL_RBO_CONFIG_ADMIN_READ : ACL_TYPE.DEAL_RBO_CONFIG_ADMIN_WRITE;

    return isAllowed(isAllowedByCompiledAcl(adminAclType));
  }, [isAllowed, readonly]);

  const { targetOptions, modelGoalOptions, channelOptions, tacticOptions } = getRboTranslations(t);

  const conditional = useCallback((element, condition: boolean) => (condition ? [element] : []), []);
  const fields: Field[] = useMemo(
    () => [
      ...conditional(
        {
          field: rboConfigField.TACTIC,
          options: tacticOptions,
        },
        isAdvancedAllowed
      ),
      ...conditional(
        {
          field: rboConfigField.HOURLY_TARGET,
        },
        isAdvancedAllowed
      ),
      ...conditional(
        {
          field: rboConfigField.DROP_PERCENT,
        },
        isAdvancedAllowed
      ),
      ...conditional(
        {
          field: rboConfigField.THRESHOLD,
        },
        isAdvancedAllowed
      ),
      {
        field: rboConfigField.CHANNEL,
        options: channelOptions,
      },
      ...conditional(
        {
          field: rboConfigField.MODEL_GOAL,
          options: modelGoalOptions,
        },
        isAdvancedAllowed
      ),
      ...conditional(
        {
          field: rboConfigField.TARGET,
          options: targetOptions,
        },
        isAdvancedAllowed
      ),
      ...conditional(
        {
          field: rboConfigField.CUSTOM_SCORE_NAME,
        },
        isAdvancedAllowed
      ),
    ],
    [conditional, isAdvancedAllowed, targetOptions, modelGoalOptions, channelOptions, tacticOptions]
  );

  return fields;
};
